/**
 * WCAG contrast helpers — framework-free (no Gatsby / theme-ui imports) so the
 * button variant/token logic (DEV-744) can be ported to `packages/ui` unchanged.
 *
 * All functions take/return plain 6- or 3-digit hex strings (with or without `#`).
 */

/**
 * Dark foreground candidate on light backgrounds. Pure black is used
 * deliberately: for the black/white pair, max contrast is ≥ 4.58:1 for *any*
 * background luminance, so filled buttons stay AA-readable even for
 * mid-luminance brand colors in their darkened hover state.
 */
export const DARK_FOREGROUND = "#000000"
/** White foreground candidate on dark backgrounds. */
export const LIGHT_FOREGROUND = "#ffffff"

/**
 * Parse a hex color to `{ r, g, b }` (0-255). Supports `#rgb` and `#rrggbb`.
 * @param {string} hex
 * @returns {{ r: number, g: number, b: number }}
 */
export function hexToRgb(hex) {
  if (typeof hex !== "string") {
    throw new TypeError(`hexToRgb expects a string, got ${typeof hex}`)
  }
  let value = hex.trim().replace(/^#/, "")
  if (value.length === 3) {
    value = value
      .split("")
      .map((c) => c + c)
      .join("")
  }
  if (!/^[0-9a-fA-F]{6}$/.test(value)) {
    throw new Error(`Invalid hex color: "${hex}"`)
  }
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  }
}

/**
 * WCAG relative luminance of a hex color.
 * @see https://www.w3.org/TR/WCAG20/#relativeluminancedef
 * @param {string} hex
 * @returns {number} 0 (black) … 1 (white)
 */
export function relativeLuminance(hex) {
  const { r, g, b } = hexToRgb(hex)
  const channel = (c) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/**
 * WCAG contrast ratio between two hex colors (1 … 21).
 * @param {string} foreground
 * @param {string} background
 * @returns {number}
 */
export function contrastRatio(foreground, background) {
  const l1 = relativeLuminance(foreground)
  const l2 = relativeLuminance(background)
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Pick the foreground candidate with the highest contrast against `background`.
 * Defaults to comparing near-black and white — for the GBA business-unit palette
 * this always yields ≥ 4.5:1, so filled buttons are readable by construction.
 * @param {string} background
 * @param {string[]} [candidates]
 * @returns {string} the winning candidate hex
 */
export function pickReadable(
  background,
  candidates = [DARK_FOREGROUND, LIGHT_FOREGROUND]
) {
  let best = candidates[0]
  let bestRatio = -1
  for (const candidate of candidates) {
    const ratio = contrastRatio(candidate, background)
    if (ratio > bestRatio) {
      bestRatio = ratio
      best = candidate
    }
  }
  return best
}

/**
 * Darken a hex color by mixing it toward black by `amount` (0…1).
 * @param {string} hex
 * @param {number} amount 0 = unchanged, 1 = black
 * @returns {string} `#rrggbb`
 */
export function darken(hex, amount) {
  const factor = Math.min(1, Math.max(0, amount))
  const { r, g, b } = hexToRgb(hex)
  const mix = (c) => Math.round(c * (1 - factor))
  const toHex = (c) => mix(c).toString(16).padStart(2, "0")
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}
